// Renders the Sentinel reply and private-note composer.

import { useRef, useState } from 'react';
import { useAtomValue } from 'jotai';
import { ArrowLeft, BookLock, ChevronDown, ChevronsUpDown, Forward, Paperclip, Plus, X } from 'lucide-react';
// import { Signature } from 'lucide-react';
import { currentUserAtom } from '@/hooks/use-auth';
import { FormatToolbar } from '@/components/message-editor/format-toolbar';
import { EmojiButton, LinkButton } from '@/components/message-editor/insert-actions';
import { useMessageEditor } from '@/components/message-editor/use-message-editor';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { SENTINEL_ATTACHMENT_ACCEPT, sentinelAttachmentContentType } from './attachment-formats';
import { CollapseBody } from './collapse-toggle';
import '@/components/message-editor/editor.css';

/** Composer modes with their dropdown labels/icons — the single source for both. */
const COMPOSER_MODE = { reply: 'reply', note: 'note' } as const;

type ComposerMode = (typeof COMPOSER_MODE)[keyof typeof COMPOSER_MODE];

const COMPOSER_MODES: { value: ComposerMode; label: string; icon: typeof Forward }[] = [
  { value: COMPOSER_MODE.reply, label: 'Reply', icon: Forward },
  { value: COMPOSER_MODE.note, label: 'Private Note', icon: BookLock },
];

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

interface ReplyEditorProps {
  draftMarkdown: string;
  /** Identity of the loaded draft — a new key reloads the editor. */
  editorKey: string;
  /** Absent for a reply the agent wrote themselves, which has no AI draft to go back to. */
  onBackToResponse?: () => void;
  /** Absent for a hand-written reply, which has no AI draft to talk about. */
  onViewAiChat?: () => void;
  onSend: (markdown: string, close: boolean, files: File[]) => void;
  /** Absent where only a reply can be sent (a follow-up), which hides the private-note mode. */
  onAddNote?: (markdown: string) => boolean | Promise<boolean>;
  sending: boolean;
  attachmentsEnabled?: boolean;
  /** Off where sending cannot also close the ticket (a follow-up), which hides Send & Close. */
  closeEnabled?: boolean;
}

const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const MAX_TOTAL_ATTACHMENT_BYTES = 20 * 1024 * 1024;

function attachmentError(files: File[]): string | null {
  if (files.length > MAX_ATTACHMENTS) return 'You can attach up to 5 files.';
  if (files.some(({ size }) => size === 0)) return 'Empty files cannot be attached.';
  if (files.some(({ size }) => size > MAX_ATTACHMENT_BYTES)) return 'Each attachment must be 10 MB or smaller.';
  if (files.reduce((total, file) => total + file.size, 0) > MAX_TOTAL_ATTACHMENT_BYTES) {
    return 'Attachments must be 20 MB or smaller in total.';
  }
  if (new Set(files.map(({ name }) => name)).size !== files.length) {
    return 'Attachment filenames must be unique.';
  }
  if (files.some((file) => sentinelAttachmentContentType(file) === null)) {
    return 'Only JPG, PNG, GIF, WEBP, PDF, DOC, DOCX, or TXT files are supported.';
  }
  return null;
}

export function ReplyEditor({
  draftMarkdown,
  editorKey,
  onBackToResponse,
  onViewAiChat,
  onSend,
  onAddNote,
  sending,
  attachmentsEnabled = false,
  closeEnabled = true,
}: ReplyEditorProps) {
  const [mode, setMode] = useState<ComposerMode>(COMPOSER_MODE.reply);
  const [replyMarkdown, setReplyMarkdown] = useState(draftMarkdown);
  const [expanded, setExpanded] = useState(false);
  // Hides the draft (kept mounted, so nothing typed is lost) to read the thread behind it.
  const [collapsed, setCollapsed] = useState(false);
  // Bumped after each note so the note editor starts clean; the reply draft is untouched.
  const [noteNonce, setNoteNonce] = useState(0);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  // Nothing flips this while the signature toggle is hidden, so no signature is appended.
  const [withSignature] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const signature = useAtomValue(currentUserAtom)?.messageSignature;
  const isNote = mode === COMPOSER_MODE.note;

  const editor = useMessageEditor({
    initialMarkdown: isNote ? '' : replyMarkdown,
    editorKey: isNote ? `${editorKey}:note:${noteNonce}` : editorKey,
    placeholder: isNote ? 'Add a private note' : 'Type your reply',
    onChange: isNote ? undefined : setReplyMarkdown,
  });

  const submitNote = async () => {
    const added = await onAddNote?.(editor.getMarkdown());
    if (added) setNoteNonce((nonce) => nonce + 1);
  };

  const send = (close: boolean) => {
    const markdown = editor.getMarkdown();
    onSend(withSignature && signature ? `${markdown}\n\n${signature}` : markdown, close, files);
  };

  const addFiles = (picked: FileList | null) => {
    if (!picked?.length) return;
    const next = [...files, ...Array.from(picked)];
    const error = attachmentError(next);
    setFileError(error);
    if (!error) setFiles(next);
  };

  return (
    <div
      className={cn(
        'flex w-full flex-col items-end gap-3 rounded-[20px] border p-2.5 shadow-[0_2px_12px_0_rgba(0,0,0,0.1)] backdrop-blur-[6px]',
        'animate-in fade-in-0 slide-in-from-bottom-2 duration-200',
        isNote ? 'border-yellow-200 bg-yellow-100' : 'border-gray-400 bg-white/80',
      )}
    >
      <div className="flex w-full flex-col gap-1">
        <div className="flex w-full items-center justify-between px-1.5">
          {onAddNote ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              {/* Both px rules needed: the chevron triggers the size's `has-[>svg]:px-2.5`, which bare `px-0` can't displace. */}
              <Button
                variant="ghost"
                size="sm"
                className="gap-0.5 px-0 has-[>svg]:px-0 text-[12px] text-gray-900 hover:bg-transparent hover:text-text-heading"
              >
                {COMPOSER_MODES.find(({ value }) => value === mode)?.label}
                <ChevronDown className="size-[14px]" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[150px] rounded-xl">
              {COMPOSER_MODES.map(({ value, label, icon: Icon }) => (
                <DropdownMenuItem
                  key={value}
                  className="gap-2.5 rounded-lg text-[13px]"
                  onSelect={() => setMode(value)}
                >
                  <Icon className="size-4 text-gray-900" />
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          ) : (
            <span className="text-[12px] text-gray-900">Reply</span>
          )}
          <div className="flex items-center gap-1">
            {!isNote && onBackToResponse && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onBackToResponse}
                className="px-2 text-[12px] font-normal text-gray-900 hover:bg-gray-100 hover:text-text-heading"
              >
                <ArrowLeft className="size-[14px]" />
                Back to AI response
              </Button>
            )}
            {!collapsed && (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setExpanded((prev) => !prev)}
                className="text-gray-900 hover:bg-gray-100 hover:text-text-heading"
                aria-label={expanded ? 'Shrink composer' : 'Expand composer'}
              >
                <ChevronsUpDown className="size-[14px]" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setCollapsed((prev) => !prev)}
              className="text-gray-900 hover:bg-gray-100 hover:text-text-heading"
              aria-expanded={!collapsed}
              aria-label={collapsed ? 'Show draft' : 'Hide draft'}
            >
              <ChevronDown className={cn('size-[14px] transition-transform duration-300', !collapsed && 'rotate-180')} />
            </Button>
          </div>
        </div>
        <CollapseBody open={!collapsed}>
          <div ref={editor.hostRef} className={cn('message-editor -mr-2.5 w-full p-1.5 pr-0', expanded && 'is-expanded')} />
        </CollapseBody>
      </div>

      {!isNote && files.length > 0 && (
        <div className="flex w-full flex-wrap gap-1.5">
          {files.map((file, index) => (
            <span
              key={`${file.name}-${index}`}
              className="flex items-center gap-1.5 rounded-full border border-gray-400 bg-white px-3 py-1 text-[12px] leading-4 text-text-body"
            >
              <Paperclip className="size-3 shrink-0 text-gray-900" />
              <span className="max-w-[160px] truncate">{file.name}</span>
              <span className="text-gray-700">{formatSize(file.size)}</span>
              <button
                type="button"
                onClick={() => {
                  setFiles((prev) => prev.filter((_, i) => i !== index));
                  setFileError(null);
                }}
                aria-label={`Remove ${file.name}`}
                className="cursor-pointer rounded-full transition-colors hover:bg-gray-100"
              >
                <X className="size-3 text-gray-900" />
              </button>
            </span>
          ))}
        </div>
      )}

      {!isNote && fileError && (
        <p role="alert" className="w-full px-1.5 text-[12px] text-red-600">
          {fileError}
        </p>
      )}

      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-1">
          {!isNote && attachmentsEnabled && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={SENTINEL_ATTACHMENT_ACCEPT}
                hidden
                onChange={(event) => {
                  addFiles(event.target.files);
                  event.target.value = '';
                }}
              />
              <Button
                variant="outline"
                size="icon-sm"
                disabled={sending || files.length >= MAX_ATTACHMENTS}
                onClick={() => fileInputRef.current?.click()}
                className="rounded-full border-gray-400 text-gray-900"
                aria-label="Add attachment"
                title="Up to 5 files, 10 MB each, 20 MB total"
              >
                <Plus className="size-4" />
              </Button>
            </>
          )}
          {!isNote && onViewAiChat && (
            <Button variant="outline" onClick={onViewAiChat} className="px-3 text-[13px] font-normal">
              View AI Chat
            </Button>
          )}
          <div className="ml-1 flex items-center gap-1">
            <FormatToolbar editor={editor} />
            <EmojiButton editor={editor} />
            <LinkButton editor={editor} />
            {/* Signature toggle hidden for now — restore this and its setter together. */}
            {/* <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setWithSignature((prev) => !prev)}
              disabled={!signature}
              aria-pressed={withSignature}
              aria-label="Append signature"
              title={signature ? 'Append signature on send' : 'No signature set'}
              className={cn(
                'size-6 text-gray-900 hover:bg-gray-100 hover:text-text-heading',
                withSignature && 'bg-gray-100 text-text-heading',
              )}
            >
              <Signature className="size-[14px]" />
            </Button> */}
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {isNote ? (
            <Button size="sm" disabled={sending} onClick={() => void submitNote()} className="text-[13px] font-normal">
              Add Note
            </Button>
          ) : (
            <>
              {closeEnabled && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={sending}
                  onClick={() => send(true)}
                  className="text-[13px] font-normal"
                >
                  Send &amp; Close
                </Button>
              )}
              <Button
                size="sm"
                disabled={sending}
                onClick={() => send(false)}
                className="text-[13px] font-normal"
              >
                Send
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
